import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Chip, IconButton, Tooltip,
  Divider, Tabs, Tab, Grid, Switch, FormControlLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import FactoryIcon from '@mui/icons-material/Factory';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const API = '/api/produccion';

const ESTADO_COLOR = {
  PENDIENTE: 'warning', EN_PROCESO: 'info', COMPLETADA: 'success', CANCELADA: 'error'
};

function fmt(v) { return parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtMon(v) { return `S/ ${fmt(v)}`; }
function hoy() { return new Date().toLocaleDateString('en-CA'); }

const FORM_INIT = {
  receta_id: '', cantidad_a_producir: '', fecha_inicio: hoy(),
  fecha_fin_estimada: '', sucursal_id: '', almacen_insumos_id: '',
  almacen_producto_id: '', observaciones: ''
};

export default function OrdenProduccion() {
  const [tab, setTab] = useState(0);
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [recetas, setRecetas] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);

  const [dlgNueva, setDlgNueva] = useState(false);
  const [form, setForm] = useState(FORM_INIT);
  const [saving, setSaving] = useState(false);

  const [dlgDetalle, setDlgDetalle] = useState(null);
  const [loadingDet, setLoadingDet] = useState(false);
  const [nuevoCosto, setNuevoCosto] = useState({ descripcion: '', tipo: 'OTROS', monto: '' });
  const [savingCosto, setSavingCosto] = useState(false);
  const [completando, setCompletando] = useState(false);

  const [reporte, setReporte] = useState([]);
  const [filtroDesde, setFiltroDesde] = useState('');
  const [filtroHasta, setFiltroHasta] = useState('');
  const [loadingRep, setLoadingRep] = useState(false);

  // PDF dialog state
  const [openOpPdfDialog, setOpenOpPdfDialog] = useState(false);
  const [opPdfTarget, setOpPdfTarget] = useState(null);
  const [opPdfFormatoSeleccionado, setOpPdfFormatoSeleccionado] = useState(() => localStorage.getItem('orden_produccion_pdf_formato_default') || 'A4');
  const [guardarOpPdfComoPredeterminado, setGuardarOpPdfComoPredeterminado] = useState(true);
  const [opPdfResolucion, setOpPdfResolucion] = useState({});
  const [opPdfResolucionCargando, setOpPdfResolucionCargando] = useState(false);

  const actualizarResolucionFormatoOp = async (nuevoFormato) => {
    if (!opPdfTarget) return;
    setOpPdfResolucionCargando(true);
    try {
      const res = await axios.get(`/api/reporte-pdf/resuelve-orden-produccion/${opPdfTarget.id}`, { params: { formato: nuevoFormato } });
      setOpPdfResolucion(res.data || {});
    } catch (err) {
      console.error('Error resolving OP PDF template:', err);
    } finally {
      setOpPdfResolucionCargando(false);
    }
  };

  const abrirDialogoPdfOp = async (op) => {
    const formatoGuardado = localStorage.getItem('orden_produccion_pdf_formato_default') || 'A4';
    setOpPdfTarget(op);
    setOpPdfFormatoSeleccionado(formatoGuardado);
    setGuardarOpPdfComoPredeterminado(true);
    setOpPdfResolucion({});
    setOpenOpPdfDialog(true);
    setOpPdfResolucionCargando(true);
    try {
      const res = await axios.get(`/api/reporte-pdf/resuelve-orden-produccion/${op.id}`, { params: { formato: formatoGuardado } });
      setOpPdfResolucion(res.data || {});
    } catch (err) {
      console.error('Error resolving OP PDF template:', err);
    } finally {
      setOpPdfResolucionCargando(false);
    }
  };

  const cerrarDialogoPdfOp = () => {
    setOpenOpPdfDialog(false);
    setOpPdfTarget(null);
  };

  const descargarPdfOp = () => {
    if (!opPdfTarget) return;
    const formato = String(opPdfFormatoSeleccionado || 'A4').toUpperCase();
    if (guardarOpPdfComoPredeterminado) {
      localStorage.setItem('orden_produccion_pdf_formato_default', formato);
    }
    const tk = localStorage.getItem('token') || '';
    const query = new URLSearchParams({ formato, mode: 'inline', _ts: Date.now() }).toString();
    window.open(`/api/reporte-pdf/orden-produccion/${opPdfTarget.id}?${query}&token=${tk}`, '_blank');
    cerrarDialogoPdfOp();
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ordenes`);
      setOrdenes(r.data);
    } catch { setError('Error al cargar órdenes'); }
    setLoading(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    axios.get(`${API}/recetas`).then(r => setRecetas(r.data.filter(r => r.estado === 'ACTIVA'))).catch(() => {});
    axios.get('/api/sucursales/mis-sucursales').then(r => setSucursales(r.data)).catch(() => {});
    axios.get('/api/almacen/mis-almacenes').then(r => setAlmacenes(r.data)).catch(() => {});
  }, []);

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function crear() {
    if (!form.receta_id || !form.cantidad_a_producir || !form.fecha_inicio)
      return setError('Receta, cantidad y fecha de inicio son requeridos');
    setSaving(true); setError('');
    try {
      const r = await axios.post(`${API}/ordenes`, form);
      setSuccess(`Orden ${r.data.numero} creada`);
      setDlgNueva(false); setForm(FORM_INIT); cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al crear'); }
    setSaving(false);
  }

  async function abrirDetalle(op) {
    setLoadingDet(true); setDlgDetalle({ id: op.id });
    try {
      const r = await axios.get(`${API}/ordenes/${op.id}`);
      setDlgDetalle(r.data);
    } catch { setError('Error al cargar detalle'); }
    setLoadingDet(false);
  }

  async function agregarCosto() {
    if (!nuevoCosto.descripcion || !nuevoCosto.monto) return;
    setSavingCosto(true);
    try {
      await axios.post(`${API}/ordenes/${dlgDetalle.id}/costos`, nuevoCosto);
      setNuevoCosto({ descripcion: '', tipo: 'OTROS', monto: '' });
      const r = await axios.get(`${API}/ordenes/${dlgDetalle.id}`);
      setDlgDetalle(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    setSavingCosto(false);
  }

  async function eliminarCosto(costoId) {
    try {
      await axios.delete(`${API}/ordenes/${dlgDetalle.id}/costos/${costoId}`);
      const r = await axios.get(`${API}/ordenes/${dlgDetalle.id}`);
      setDlgDetalle(r.data);
    } catch { setError('Error al eliminar costo'); }
  }

  async function completarOrden() {
    if (!window.confirm('¿Completar esta orden? Se consumirán los insumos del kardex y se registrará el ingreso del producto terminado.')) return;
    setCompletando(true);
    try {
      const r = await axios.post(`${API}/ordenes/${dlgDetalle.id}/completar`);
      setSuccess(`Orden completada. Costo total: ${fmtMon(r.data.costoTotal)} — Unitario: ${fmtMon(r.data.costoUnitario)}`);
      setDlgDetalle(null); cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al completar'); }
    setCompletando(false);
  }

  async function cancelarOrden(op) {
    if (!window.confirm(`¿Cancelar la orden ${op.numero}?`)) return;
    try {
      await axios.post(`${API}/ordenes/${op.id}/cancelar`);
      setSuccess('Orden cancelada'); cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al cancelar'); }
  }

  async function cargarReporte() {
    setLoadingRep(true);
    try {
      const params = new URLSearchParams();
      if (filtroDesde) params.append('desde', filtroDesde);
      if (filtroHasta) params.append('hasta', filtroHasta);
      const r = await axios.get(`${API}/reporte-costos?${params}`);
      setReporte(r.data);
    } catch { setError('Error al cargar reporte'); }
    setLoadingRep(false);
  }

  const totalReporte = reporte.reduce((s, r) => ({
    costo_insumos: s.costo_insumos + parseFloat(r.costo_insumos || 0),
    costo_adicional: s.costo_adicional + parseFloat(r.costo_adicional || 0),
    costo_total: s.costo_total + parseFloat(r.costo_total || 0),
  }), { costo_insumos: 0, costo_adicional: 0, costo_total: 0 });

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <FactoryIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">Órdenes de Producción</Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={cargar} disabled={loading}><RefreshIcon /></IconButton>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm(FORM_INIT); setDlgNueva(true); }}>
          Nueva Orden
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Órdenes" />
        <Tab label="Reporte de Costos" icon={<AssessmentIcon />} iconPosition="start" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* TAB 0 — LISTADO */}
      {tab === 0 && (
        loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} /> : (
          <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  {['N° Orden', 'Receta', 'Producto', 'Cantidad', 'F. Inicio', 'Costo Total', 'Estado', 'Acc.'].map(h => (
                    <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {ordenes.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay órdenes registradas</TableCell></TableRow>
                ) : ordenes.map(op => (
                  <TableRow key={op.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{op.numero}</TableCell>
                    <TableCell>{op.receta_nombre}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{op.producto_nombre}</Typography>
                      <Typography variant="caption" color="text.secondary">{op.producto_codigo}</Typography>
                    </TableCell>
                    <TableCell align="right">{fmt(op.cantidad_a_producir)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{op.fecha_inicio?.slice(0, 10)}</TableCell>
                    <TableCell align="right">{op.costo_total > 0 ? fmtMon(op.costo_total) : '—'}</TableCell>
                    <TableCell align="center">
                      <Chip label={op.estado} size="small" color={ESTADO_COLOR[op.estado] || 'default'} />
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Ver / Gestionar"><IconButton size="small" color="info" onClick={() => abrirDetalle(op)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="PDF"><IconButton size="small" color="secondary" onClick={() => abrirDialogoPdfOp(op)}><PictureAsPdfIcon fontSize="small" /></IconButton></Tooltip>
                      {(op.estado === 'PENDIENTE' || op.estado === 'EN_PROCESO') && (
                        <Tooltip title="Cancelar"><IconButton size="small" color="error" onClick={() => cancelarOrden(op)}><CancelIcon fontSize="small" /></IconButton></Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* TAB 1 — REPORTE DE COSTOS */}
      {tab === 1 && (
        <Box>
          <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }}
                value={filtroDesde} onChange={e => setFiltroDesde(e.target.value)} sx={{ minWidth: '12em' }} />
              <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }}
                value={filtroHasta} onChange={e => setFiltroHasta(e.target.value)} sx={{ minWidth: '12em' }} />
              <Button variant="contained" startIcon={<AssessmentIcon />} onClick={cargarReporte} disabled={loadingRep}>
                {loadingRep ? <CircularProgress size={18} color="inherit" /> : 'Generar Reporte'}
              </Button>
            </Box>
          </Paper>

          {reporte.length > 0 && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {[
                  { label: 'Total órdenes', value: reporte.length, color: 'primary.main' },
                  { label: 'Costo insumos', value: fmtMon(totalReporte.costo_insumos), color: 'text.primary' },
                  { label: 'Costos adicionales', value: fmtMon(totalReporte.costo_adicional), color: 'warning.main' },
                  { label: 'Costo total', value: fmtMon(totalReporte.costo_total), color: 'success.main' },
                ].map((k, i) => (
                  <Grid key={i} item xs={6} sm={3}>
                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                      <Typography fontWeight="bold" color={k.color}>{k.value}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      {['N° Orden', 'Producto', 'Cantidad', 'F. Inicio', 'Costo Insumos', 'Costo Adicional', 'Costo Total', 'Costo Unitario'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reporte.map(r => (
                      <TableRow key={r.numero} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.numero}</TableCell>
                        <TableCell>{r.producto_nombre}</TableCell>
                        <TableCell align="right">{fmt(r.cantidad_producida)}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{r.fecha_inicio?.slice(0, 10)}</TableCell>
                        <TableCell align="right">{fmtMon(r.costo_insumos)}</TableCell>
                        <TableCell align="right">{fmtMon(r.costo_adicional)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmtMon(r.costo_total)}</TableCell>
                        <TableCell align="right">{fmtMon(r.costo_unitario)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      )}

      {/* Dialog Nueva Orden */}
      <Dialog open={dlgNueva} onClose={() => setDlgNueva(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva Orden de Producción</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, pt: 1 }}>
            <TextField select size="small" fullWidth label="Receta *" value={form.receta_id}
              onChange={e => setF('receta_id', e.target.value)}>
              {recetas.map(r => (
                <MenuItem key={r.id} value={r.id}>{r.codigo} — {r.nombre} ({r.producto_nombre})</MenuItem>
              ))}
            </TextField>
            <TextField size="small" label="Cantidad a producir *" type="number"
              value={form.cantidad_a_producir} onChange={e => setF('cantidad_a_producir', e.target.value)} sx={{ flex: 1, minWidth: '10em' }} />
            <TextField size="small" type="date" label="Fecha inicio *" InputLabelProps={{ shrink: true }}
              value={form.fecha_inicio} onChange={e => setF('fecha_inicio', e.target.value)} sx={{ minWidth: '12em' }} />
            <TextField size="small" type="date" label="Fecha fin estimada" InputLabelProps={{ shrink: true }}
              value={form.fecha_fin_estimada} onChange={e => setF('fecha_fin_estimada', e.target.value)} sx={{ minWidth: '12em' }} />
            <TextField select size="small" fullWidth label="Sucursal" value={form.sucursal_id}
              onChange={e => setF('sucursal_id', e.target.value)}>
              <MenuItem value="">— Sin sucursal —</MenuItem>
              {sucursales.map(s => <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>)}
            </TextField>
            <TextField select size="small" sx={{ flex: 1, minWidth: '15em' }} label="Almacén de insumos" value={form.almacen_insumos_id}
              onChange={e => setF('almacen_insumos_id', e.target.value)}>
              <MenuItem value="">— Sin almacén —</MenuItem>
              {almacenes.map(a => <MenuItem key={a.id} value={a.id}>{a.nombre}</MenuItem>)}
            </TextField>
            <TextField select size="small" sx={{ flex: 1, minWidth: '15em' }} label="Almacén producto terminado" value={form.almacen_producto_id}
              onChange={e => setF('almacen_producto_id', e.target.value)}>
              <MenuItem value="">— Sin almacén —</MenuItem>
              {almacenes.map(a => <MenuItem key={a.id} value={a.id}>{a.nombre}</MenuItem>)}
            </TextField>
            <TextField size="small" fullWidth label="Observaciones" multiline rows={2}
              value={form.observaciones} onChange={e => setF('observaciones', e.target.value)} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgNueva(false)}>Cancelar</Button>
          <Button variant="contained" onClick={crear} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Crear Orden'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Detalle / Gestión */}
      <Dialog open={!!dlgDetalle} onClose={() => setDlgDetalle(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <FactoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {dlgDetalle?.numero} — {dlgDetalle?.receta_nombre}
          </Box>
          <IconButton onClick={() => setDlgDetalle(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDet ? <CircularProgress sx={{ display: 'block', mx: 'auto', my: 3 }} /> : dlgDetalle?.receta_nombre && (
            <Box>
              {/* Cabecera info */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Producto</Typography><Typography fontWeight={600}>{dlgDetalle.producto_nombre}</Typography></Grid>
                <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Cantidad</Typography><Typography fontWeight={600}>{fmt(dlgDetalle.cantidad_a_producir)}</Typography></Grid>
                <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Estado</Typography><br /><Chip label={dlgDetalle.estado} size="small" color={ESTADO_COLOR[dlgDetalle.estado] || 'default'} /></Grid>
                <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Fecha inicio</Typography><Typography>{dlgDetalle.fecha_inicio?.slice(0, 10)}</Typography></Grid>
              </Grid>

              <Divider sx={{ my: 1.5 }}>
                <Typography variant="caption" fontWeight="bold">Insumos Requeridos</Typography>
              </Divider>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell>Insumo</TableCell>
                    <TableCell align="right">Cant. Requerida</TableCell>
                    <TableCell align="right">Costo Unit.</TableCell>
                    <TableCell align="right">Costo Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(dlgDetalle.insumos || []).map(ins => (
                    <TableRow key={ins.id}>
                      <TableCell>{ins.insumo_nombre} <Typography variant="caption" color="text.secondary">{ins.insumo_codigo}</Typography></TableCell>
                      <TableCell align="right">{fmt(ins.cantidad_requerida)} {ins.unidad_abreviatura || ''}</TableCell>
                      <TableCell align="right">{fmtMon(ins.costo_unitario)}</TableCell>
                      <TableCell align="right">{fmtMon(ins.costo_total)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>Subtotal insumos:</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmtMon(dlgDetalle.costo_insumos)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Divider sx={{ my: 1.5 }}>
                <Typography variant="caption" fontWeight="bold">Costos Adicionales (MOO, CIF)</Typography>
              </Divider>
              {(dlgDetalle.costos_adicionales || []).length > 0 && (
                <Table size="small" sx={{ mb: 1.5 }}>
                  <TableBody>
                    {dlgDetalle.costos_adicionales.map(c => (
                      <TableRow key={c.id}>
                        <TableCell>{c.descripcion}</TableCell>
                        <TableCell><Chip label={c.tipo} size="small" /></TableCell>
                        <TableCell align="right">{fmtMon(c.monto)}</TableCell>
                        <TableCell align="center">
                          {dlgDetalle.estado !== 'COMPLETADA' && dlgDetalle.estado !== 'CANCELADA' && (
                            <IconButton size="small" color="error" onClick={() => eliminarCosto(c.id)}><DeleteIcon fontSize="small" /></IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {dlgDetalle.estado !== 'COMPLETADA' && dlgDetalle.estado !== 'CANCELADA' && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <TextField size="small" label="Descripción" value={nuevoCosto.descripcion}
                    onChange={e => setNuevoCosto(c => ({ ...c, descripcion: e.target.value }))} sx={{ flex: 1, minWidth: '12em' }} />
                  <TextField select size="small" label="Tipo" value={nuevoCosto.tipo}
                    onChange={e => setNuevoCosto(c => ({ ...c, tipo: e.target.value }))} sx={{ minWidth: '8em' }}>
                    <MenuItem value="MOO">MOO</MenuItem>
                    <MenuItem value="CIF">CIF</MenuItem>
                    <MenuItem value="OTROS">Otros</MenuItem>
                  </TextField>
                  <TextField size="small" label="Monto S/" type="number" value={nuevoCosto.monto}
                    onChange={e => setNuevoCosto(c => ({ ...c, monto: e.target.value }))} sx={{ width: '9em' }} />
                  <Button variant="outlined" size="small" onClick={agregarCosto} disabled={savingCosto} startIcon={<AddIcon />}>
                    {savingCosto ? <CircularProgress size={16} /> : 'Agregar'}
                  </Button>
                </Box>
              )}

              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <Box><Typography variant="caption" color="text.secondary">Costo insumos</Typography><Typography fontWeight={600}>{fmtMon(dlgDetalle.costo_insumos)}</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary">Costos adicionales</Typography><Typography fontWeight={600}>{fmtMon(dlgDetalle.costo_adicional)}</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary">Costo total</Typography><Typography fontWeight={700} color="primary.main">{fmtMon(dlgDetalle.costo_total)}</Typography></Box>
                  {dlgDetalle.costo_unitario > 0 && (
                    <Box><Typography variant="caption" color="text.secondary">Costo unitario</Typography><Typography fontWeight={700} color="success.main">{fmtMon(dlgDetalle.costo_unitario)}</Typography></Box>
                  )}
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgDetalle(null)}>Cerrar</Button>
          {dlgDetalle?.estado === 'PENDIENTE' || dlgDetalle?.estado === 'EN_PROCESO' ? (
            <Button variant="contained" color="success" startIcon={<CheckCircleIcon />}
              onClick={completarOrden} disabled={completando}>
              {completando ? <CircularProgress size={20} color="inherit" /> : 'Completar Orden'}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      {/* PDF Format Dialog - Orden de Producción */}
      <Dialog open={openOpPdfDialog} onClose={cerrarDialogoPdfOp} maxWidth="sm" fullWidth>
        <DialogTitle>Descargar PDF - Orden de Producción</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Selecciona el formato de impresión. El formato elegido puede quedar como predeterminado.
          </Alert>
          {opPdfResolucionCargando ? (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">Resolviendo plantilla...</Typography>
            </Box>
          ) : opPdfResolucion && opPdfResolucion.plantilla_nombre ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2"><strong>Plantilla:</strong> {opPdfResolucion.plantilla_nombre}</Typography>
              <Typography variant="body2"><strong>Ámbito:</strong> {opPdfResolucion.scope === 'Global' ? 'Global (todas las sucursales)' : opPdfResolucion.scope === 'Specific' ? 'Específica para esta sucursal' : opPdfResolucion.scope}</Typography>
              {opPdfResolucion.is_inherited && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#1976d2' }}>
                  Esta sucursal hereda la asignación de la configuración global
                </Typography>
              )}
            </Alert>
          ) : null}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5, mb: 2 }}>
            {[
              { codigo: 'A4', titulo: 'A4', detalle: 'Formato completo' },
              { codigo: 'A5', titulo: 'A5', detalle: 'Formato compacto' },
              { codigo: 'T80', titulo: 'Ticket 80mm', detalle: 'Impresora térmica' }
            ].map((item) => (
              <Paper
                key={item.codigo}
                onClick={() => {
                  setOpPdfFormatoSeleccionado(item.codigo);
                  actualizarResolucionFormatoOp(item.codigo);
                }}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: opPdfFormatoSeleccionado === item.codigo ? '#1976d2' : '#d0d7de',
                  backgroundColor: opPdfFormatoSeleccionado === item.codigo ? '#e3f2fd' : '#fff'
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>{item.titulo}</Typography>
                <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>{item.detalle}</Typography>
                {localStorage.getItem('orden_produccion_pdf_formato_default') === item.codigo && (
                  <Chip label="Predeterminado" color="primary" size="small" sx={{ mt: 1, display: 'flex', mx: 'auto', width: 'fit-content' }} />
                )}
              </Paper>
            ))}
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={guardarOpPdfComoPredeterminado}
                onChange={(e) => setGuardarOpPdfComoPredeterminado(e.target.checked)}
                color="primary"
              />
            }
            label={`Guardar ${opPdfFormatoSeleccionado} como formato predeterminado`}
          />
          {opPdfTarget && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Documento: OP-{String(opPdfTarget.numero || opPdfTarget.id || '')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDialogoPdfOp}>Cancelar</Button>
          <Button onClick={descargarPdfOp} variant="contained">
            Descargar {opPdfFormatoSeleccionado}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
